import './polyfills'
import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import Amplify, { API } from 'aws-amplify';
import aws_exports from './aws-exports';
import { UserProvider } from './infrastructure/UserProvider';
import { Toaster } from "react-hot-toast";
import { getSessionToken, checkSessionError, clearSessionToken, setSessionConflict } from './infrastructure/SessionManager';
import { removeUserCookies } from './infrastructure/UserAuthentication';
import { Auth } from 'aws-amplify';
import { validateSession } from './graphql/mutations';

Amplify.configure({
  ...aws_exports,
  API: {
    ...aws_exports.API,
    graphql_headers: async () => {
      const sessionToken = getSessionToken();
      return sessionToken ? { 'x-session-token': sessionToken } : {};
    }
  }
});

// Global interceptor: catch session errors AND validate before every call
const originalGraphql = API.graphql.bind(API);

// Mutations that should NOT trigger session pre-check
const UNPROTECTED_MUTATIONS = [
  'loginWithOtp', 'OtpVerification', 'signupWithOtp', 'signupOtpVerification',
  'createSession', 'validateSession', 'checkUserInput', 'checkIdentificationDuplication',
  'adminGenerateMfa', 'adminValidateMfa', 'getGeneralConfig', 'getUserRecord',
  'createUserLambda', 'createPaymentTransactionRecord',
  'getUserPaymentTransactionRecord', 'sendReceipt', 'getReceipt',
  'checkPaymentStatus', 'updatePaymentStatus'
];

API.graphql = async (...args) => {
  // Pre-check: validate session before making the actual call
  const token = getSessionToken();

  // Skip pre-check for unprotected mutations or if no token
  const queryStr = typeof args[0] === 'object' ? (args[0].query || '') : (args[0] || '');
  const isUnprotected = UNPROTECTED_MUTATIONS.some(m => queryStr.includes(m));

  if (token && !isUnprotected) {
    try {
      const checkResponse = await originalGraphql({
        query: validateSession,
        variables: { sessionToken: token, readOnly: true },
        authMode: 'API_KEY'
      });
      const checkResult = checkResponse.data.validateSession;
      if (checkResult && checkResult.isValid === false) {
        const message = checkResult.message === 'Session has been invalidated'
          ? 'Your session has been terminated because your account was logged in from another location.'
          : 'Your session has expired. Please log in again.';
        clearSessionToken();
        removeUserCookies();
        sessionStorage.removeItem('mfaVerified');
        setSessionConflict(message);
        try { Auth.signOut(); } catch (e) {}
        const isAdminPath = window.location.pathname.startsWith('/admin');
        window.location.href = isAdminPath ? '/admin/login' : '/login';
        return null;
      }
    } catch (e) {
      // Silent fail on pre-check
    }
  }

  try {
    const result = await originalGraphql(...args);
    return result;
  } catch (error) {
    const sessionError = checkSessionError(error);
    if (sessionError) {
      const message = sessionError === 'Session has been invalidated'
        ? 'Your session has been terminated because your account was logged in from another location.'
        : 'Your session has expired. Please log in again.';
      clearSessionToken();
      removeUserCookies();
      sessionStorage.removeItem('mfaVerified');
      setSessionConflict(message);
      try { Auth.signOut(); } catch (e) {}
      const isAdminPath = window.location.pathname.startsWith('/admin');
      window.location.href = isAdminPath ? '/admin/login' : '/login';
      return null;
    }
    throw error;
  }
};

// Periodic session validation check (every 30 seconds)
// This catches session invalidation even on pages using DynamoDB resolvers
setInterval(async () => {
  const token = getSessionToken();
  if (!token) return; // Not logged in, skip

  try {
    const response = await originalGraphql({
      query: validateSession,
      variables: { sessionToken: token, readOnly: true },
      authMode: 'API_KEY'
    });

    const result = response.data.validateSession;
    if (result && result.isValid === false) {
      const message = result.message === 'Session has been invalidated'
        ? 'Your session has been terminated because your account was logged in from another location.'
        : 'Your session has expired. Please log in again.';
      clearSessionToken();
      removeUserCookies();
      sessionStorage.removeItem('mfaVerified');
      setSessionConflict(message);
      try { Auth.signOut(); } catch (e) {}
      const isAdminPath = window.location.pathname.startsWith('/admin');
      window.location.href = isAdminPath ? '/admin/login' : '/login';
    }
  } catch (e) {
    // Silent fail — don't disrupt the app if validation check fails
  }
}, 30000);

ReactDOM.render(
  <UserProvider>
    <App />
    <Toaster />
  </UserProvider>,
  document.getElementById("root")
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
